import { Close } from '@carbon/icons-react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
  type Placement,
} from '@floating-ui/react';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/shared/lib/cx';
import { useControllableState, usePrefersReducedMotion } from '@/shared/lib/hooks';
import { IconButton } from './IconButton';
import s from './Overlay.module.css';

export interface PopoverProps {
  /** Кнопка-триггер: получает ref и обработчики. */
  trigger: (props: Record<string, unknown> & { ref: (node: Element | null) => void; 'aria-expanded': boolean }) => ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  title?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  /** Тело без внутренних отступов (списки, таблицы). */
  flush?: boolean;
  width?: number;
  className?: string;
}

/**
 * Неблокирующее всплывающее окно у кнопки (центр уведомлений, фильтры): фокус переходит внутрь,
 * Esc и щелчок вне окна закрывают, фокус возвращается на кнопку.
 */
export function Popover({ trigger, children, title, open: openProp, onOpenChange, placement = 'bottom-end', flush = false, width, className }: PopoverProps) {
  const { t } = useTranslation('ui');
  const [open, setOpen] = useControllableState({ value: openProp, defaultValue: false, onChange: onOpenChange });
  const reducedMotion = usePrefersReducedMotion();
  const titleId = useId();
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    /* Позиция — через left/top: свойство transform занято анимацией появления (иначе окно уезжает в угол). */
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      sizeMiddleware({
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(200, availableHeight - 8)}px`;
        },
        padding: 8,
      }),
    ],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : { open: 150, close: 110 },
    initial: { opacity: 0, transform: 'translateY(-4px)' },
  });
  const close = () => setOpen(false);

  return (
    <>
      {trigger({ ...getReferenceProps(), ref: refs.setReference, 'aria-expanded': open })}
      {isMounted ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className={cx(s.popover, className)}
              style={{ ...floatingStyles, ...styles, ...(width ? { width } : {}) }}
              aria-labelledby={title ? titleId : undefined}
              {...getFloatingProps()}
            >
              {title ? (
                <div className={s.popoverHeader}>
                  <h2 id={titleId} className={s.popoverTitle}>
                    {title}
                  </h2>
                  <IconButton label={t('close')} icon={Close} size="sm" iconSize={16} onClick={close} tooltipPlacement="left" />
                </div>
              ) : null}
              <div className={flush ? s.popoverBodyFlush : s.popoverBody}>{typeof children === 'function' ? children(close) : children}</div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
}
